import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { t } from '@aquilixshop/i18n';
import { resolveColor } from '../../theme/colorTokens.js';
import { marginFields } from '../_shared/blockFields.js';
import { marginStyle, num, str } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { productImageAlt } from '../_shared/imageAlt.js';
import { fixedWidthImgProps } from '../_shared/assetVariant.js';
/** La vignette produit de la barre collante, miroir exact de `.__thumb{width:46px}`. */
const STICKY_THUMB_PAINTED_PX = 46;
/**
 * Shrine `sticky-atc` (Sticky Add To Cart) — config-by-config. A bar pinned to the bottom of the viewport
 * that surfaces once the customer scrolls past the main buy button, mirroring the product (mini thumbnail
 * + title + subtitle + rating + price + optional variant picker/qty) and re-presenting the Add-to-cart
 * action so it is always one tap away. It honours Shrine's full per-device authoring matrix: a separate
 * Desktop-layout and Mobile-layout group each toggle image / title / subtitle / rating / price / sale
 * badge / variant picker / full-button-width / price-in-button / transparent-background independently, so
 * the bar can read completely differently on phone vs desktop (Shrine renders both variants and a CSS
 * media query hides the wrong one — we do the same via a scoped `@media` + `@container aqx-page` at 750px).
 *
 * REVEAL: Shrine reveals the bar with `display_when` = always / after_scroll (once the in-page buy button
 * leaves the viewport) / after_pixels / after_percent. SSR renders the bar HIDDEN (a fixed-position bar on
 * first paint would cover content and is wrong before any scroll); a small client effect (Intersection
 * Observer for after_scroll, a scroll listener for the pixel/percent thresholds) flips it visible. With JS
 * off the bar simply never appears — the in-page buy button is the canonical CTA, so this degrades cleanly.
 * `always` shows the bar immediately on mount (still client-gated so SSR markup stays inert).
 *
 * ID MAPPING (our id ← Shrine id, kept stable): displayWhen←display_when, displayPixels←display_pixels,
 * displayPercent←display_percent, colorScheme←color_scheme, title←title, subtitle←subtitle,
 * function←function, buttonLabel←button_label, enableCustomBtnColor←enable_custom_btn_color,
 * customBtnColor←custom_btn_color, prefixIcon←prefix_icon, suffixIcon←suffix_icon, iconScale←icon_scale,
 * starColor←star_color, starsLabel←stars_label, desktopLayout←desktop_layout,
 * desktopMaxWidth←desktop_max_width, desktopShowImage←desktop_show_image, … (every desktop_ / mobile_
 * toggle 1:1), with `…ShowSubtitle` gated on subtitle!=blank exactly as Shrine.
 *
 * DEFERRED (justified):
 *  - Reveal/transition ANIMATION (Shrine slides/fades the bar in) — animation polish, not a setting in this
 *    inventory; we toggle visibility instantly (correct + SSR-safe). The threshold LOGIC is implemented.
 *  - `prefix_icon` / `suffix_icon` are Shopify image_picker (asset/file picker) — modelled here as our
 *    `image` field (URL-backed) so the control + render exist; the Shopify file-library picker UI is the
 *    platform piece we don't ship. Material-Symbols glyph icons are NOT used (inline SVG star only).
 *  - `function` scroll targets reference Shrine section IDs (#ProductInfo-id / #variant-selects-id /
 *    #quantity-breaks-id / .pricing-table). We smooth-scroll to the matching selector when present; the
 *    `add_to_cart` value does nothing yet (the real cart mutation is wired elsewhere — we render the
 *    button + bind it to ProductState, the AJAX cart write is deferred).
 */
/** Shrine color_scheme select (adds `inverse` over the generic scheme list). */
const SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background' },
    { value: 'background-2', label: 'Surface' },
    { value: 'inverse', label: 'Inverse' },
];
export const stickyAtcBlockSchema = {
    type: 'sticky-atc',
    name: 'Sticky Add To Cart',
    max: 1,
    fields: [
        // ── General ──────────────────────────────────────────────────────────────
        {
            id: 'displayWhen',
            aiConfigurable: true,
            type: 'select',
            label: 'Display when',
            group: 'General',
            default: 'after_scroll',
            options: [
                { value: 'always', label: 'Always' },
                { value: 'after_scroll', label: 'After scrolling past the buy button' },
                { value: 'after_pixels', label: 'After scrolling a number of pixels' },
                { value: 'after_percent', label: 'After scrolling a percentage of the page' },
            ],
        },
        {
            id: 'displayPixels',
            type: 'range',
            label: 'Display after pixels',
            group: 'General',
            min: 50,
            max: 2000,
            step: 50,
            default: 500,
            unit: 'px',
            visibleWhen: { field: 'displayWhen', equals: 'after_pixels' },
        },
        {
            id: 'displayPercent',
            type: 'range',
            label: 'Display after page percentage',
            group: 'General',
            min: 5,
            max: 95,
            step: 5,
            default: 50,
            unit: '%',
            visibleWhen: { field: 'displayWhen', equals: 'after_percent' },
        },
        {
            id: 'colorScheme',
            aiConfigurable: true,
            type: 'select',
            label: 'Color scheme',
            group: 'General',
            default: 'background-1',
            options: SCHEME_OPTIONS,
        },
        {
            id: 'title',
            type: 'text',
            label: 'Title',
            group: 'General',
            info: 'Overrides the product title shown in the bar. Leave empty to use the product title.',
            default: '',
            maxLength: 120,
        },
        {
            id: 'subtitle',
            type: 'text',
            label: 'Subtitle',
            group: 'General',
            default: '',
            maxLength: 120,
        },
        // ── Button ───────────────────────────────────────────────────────────────
        {
            id: 'function',
            type: 'select',
            label: 'Button function',
            group: 'Button',
            info: 'Add to cart, or smooth-scroll to a section of the product page.',
            default: 'add_to_cart',
            options: [
                { value: 'add_to_cart', label: 'Add to cart' },
                { value: 'scroll_#ProductInfo-id', label: 'Scroll to product info' },
                { value: 'scroll_#variant-selects-id', label: 'Scroll to variant selectors' },
                { value: 'scroll_#quantity-breaks-id', label: 'Scroll to quantity breaks' },
                { value: 'scroll_.pricing-table', label: 'Scroll to pricing table' },
            ],
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: 'Button',
            default: 'Ajouter au panier',
            defaultKey: 'product.add_to_cart',
            maxLength: 60,
        },
        {
            id: 'enableCustomBtnColor',
            type: 'checkbox',
            label: 'Enable custom button color',
            group: 'Button',
            default: false,
        },
        {
            id: 'customBtnColor',
            type: 'color',
            label: 'Button custom color',
            group: 'Button',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'enableCustomBtnColor', equals: true },
        },
        {
            id: 'prefixIcon',
            type: 'image',
            label: 'Prefix icon',
            group: 'Button',
            info: 'Small icon shown before the button label.',
        },
        {
            id: 'suffixIcon',
            type: 'image',
            label: 'Suffix icon',
            group: 'Button',
            info: 'Small icon shown after the button label.',
        },
        {
            id: 'iconScale',
            type: 'range',
            label: 'Icons scale',
            group: 'Button',
            info: 'Related to the button label font size.',
            min: 80,
            max: 180,
            step: 5,
            default: 120,
            unit: '%',
        },
        // ── Rating stars ─────────────────────────────────────────────────────────
        {
            id: 'starColor',
            type: 'color',
            label: 'Stars color',
            group: 'Rating stars',
            default: { ref: 'custom', value: '#ffcc00' },
        },
        {
            id: 'starsLabel',
            type: 'text',
            label: 'Stars label',
            group: 'Rating stars',
            default: '(xxxx avis)',
            defaultKey: 'sticky.reviews_placeholder',
            maxLength: 60,
        },
        // ── Desktop layout ───────────────────────────────────────────────────────
        {
            id: 'desktopLayout',
            type: 'select',
            label: 'Layout',
            group: 'Desktop layout',
            default: 'full',
            options: [
                { value: 'full', label: 'Full width' },
                { value: 'floating', label: 'Floating' },
            ],
        },
        {
            id: 'desktopMaxWidth',
            type: 'range',
            label: 'Max width',
            group: 'Desktop layout',
            min: 400,
            max: 1400,
            step: 50,
            default: 850,
            unit: 'px',
            visibleWhen: { field: 'desktopLayout', equals: 'floating' },
        },
        {
            id: 'desktopShowImage',
            type: 'checkbox',
            label: 'Show image',
            group: 'Desktop layout',
            default: false,
        },
        {
            id: 'desktopShowTitle',
            type: 'checkbox',
            label: 'Show title',
            group: 'Desktop layout',
            default: true,
        },
        {
            id: 'desktopShowSubtitle',
            type: 'checkbox',
            label: 'Show subtitle',
            group: 'Desktop layout',
            default: true,
            visibleWhen: { field: 'subtitle', notEquals: '' },
        },
        {
            id: 'desktopRatingStars',
            type: 'checkbox',
            label: 'Show rating stars',
            group: 'Desktop layout',
            default: false,
        },
        {
            id: 'desktopShowPrice',
            type: 'checkbox',
            label: 'Show price',
            group: 'Desktop layout',
            default: true,
        },
        {
            id: 'desktopShowSaleBadge',
            type: 'checkbox',
            label: 'Show sale badge',
            group: 'Desktop layout',
            default: true,
        },
        {
            id: 'desktopVariantPicker',
            type: 'checkbox',
            label: 'Show variant picker',
            group: 'Desktop layout',
            default: false,
        },
        {
            id: 'desktopFullButtonWidth',
            type: 'checkbox',
            label: 'Full width button',
            group: 'Desktop layout',
            default: false,
        },
        {
            id: 'desktopShowPriceInButton',
            type: 'checkbox',
            label: 'Show price inside the button',
            group: 'Desktop layout',
            default: false,
        },
        {
            id: 'desktopTransparentBg',
            type: 'checkbox',
            label: 'Transparent background',
            group: 'Desktop layout',
            default: false,
        },
        // ── Mobile layout ────────────────────────────────────────────────────────
        {
            id: 'mobileLayout',
            type: 'select',
            label: 'Layout',
            group: 'Mobile layout',
            default: 'full',
            options: [
                { value: 'full', label: 'Full width' },
                { value: 'floating', label: 'Floating' },
            ],
        },
        {
            id: 'mobileShowImage',
            type: 'checkbox',
            label: 'Show image',
            group: 'Mobile layout',
            default: false,
        },
        {
            id: 'mobileShowTitle',
            type: 'checkbox',
            label: 'Show title',
            group: 'Mobile layout',
            default: true,
        },
        {
            id: 'mobileShowSubtitle',
            type: 'checkbox',
            label: 'Show subtitle',
            group: 'Mobile layout',
            default: true,
            visibleWhen: { field: 'subtitle', notEquals: '' },
        },
        {
            id: 'mobileRatingStars',
            type: 'checkbox',
            label: 'Show rating stars',
            group: 'Mobile layout',
            default: false,
        },
        {
            id: 'mobileShowPrice',
            type: 'checkbox',
            label: 'Show price',
            group: 'Mobile layout',
            default: true,
        },
        {
            id: 'mobileShowSaleBadge',
            type: 'checkbox',
            label: 'Show sale badge',
            group: 'Mobile layout',
            default: true,
        },
        {
            id: 'mobileVariantPicker',
            type: 'checkbox',
            label: 'Show variant picker',
            group: 'Mobile layout',
            default: false,
        },
        {
            id: 'mobileFullButtonWidth',
            type: 'checkbox',
            label: 'Full width button',
            group: 'Mobile layout',
            default: false,
        },
        {
            id: 'mobileShowPriceInButton',
            type: 'checkbox',
            label: 'Show price inside the button',
            group: 'Mobile layout',
            default: false,
        },
        {
            id: 'mobileTransparentBg',
            type: 'checkbox',
            label: 'Transparent background',
            group: 'Mobile layout',
            default: false,
        },
        ...marginFields(0, 0),
    ],
};
// Star SVG (Shrine viewBox 0 0 576 512) — fractional overlay clipped to the rating width.
const STAR_FULL = 'M259.3 17.8 194 150.2 47.9 171.4c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46.4 46.4 33.7L288 439.6l130.7 68.7c23.2 12.1 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.7-57.4 0z';
/** Inline 5-star row with a fractional coloured overlay over a muted underlay. */
function Stars({ color, rating }) {
    const visible = Math.max(1, Math.ceil(rating));
    const overlayWidth = `calc(${rating} * 1em + ${visible - 1} * 0.1em)`;
    const Row = ({ fill }) => (_jsx(_Fragment, { children: [0, 1, 2, 3, 4].map((i) => (_jsx("svg", { viewBox: "0 0 576 512", "aria-hidden": "true", style: {
                width: '1em',
                height: '1em',
                flexShrink: 0,
                fill,
                marginInlineStart: i ? '0.1em' : 0,
            }, children: _jsx("path", { d: STAR_FULL }) }, i))) }));
    return (_jsxs("span", { style: { position: 'relative', display: 'inline-flex', fontSize: '0.85em', lineHeight: 1 }, children: [_jsx("span", { style: { display: 'inline-flex', opacity: 0.25 }, children: _jsx(Row, { fill: color }) }), _jsx("span", { style: {
                    position: 'absolute',
                    insetInlineStart: 0,
                    top: 0,
                    overflow: 'hidden',
                    display: 'inline-flex',
                    width: overlayWidth,
                }, children: _jsx(Row, { fill: color }) })] }));
}
/** Resolve a #rrggbb hex from a ColorValue/string (SSR-safe). */
function resolveHex(v, fallback) {
    if (v && typeof v === 'object' && 'value' in v) {
        const val = v.value;
        if (typeof val === 'string')
            return val;
    }
    if (typeof v === 'string')
        return v;
    return fallback;
}
/** Resolve an `image` field's URL (ImageValue carries `url`); empty string when unset. */
function imageUrl(v) {
    if (v && typeof v === 'object') {
        const o = v;
        if (typeof o.url === 'string')
            return o.url;
        if (typeof o.src === 'string')
            return o.src;
    }
    return typeof v === 'string' ? v : '';
}
/** Resolve the color_scheme select → {bg, fg} for the bar surface (`inverse` flips). */
function schemeSurface(value) {
    switch (value) {
        case 'inverse':
            return { bg: 'var(--aqx-scheme-text)', fg: 'var(--aqx-scheme-bg)' };
        case 'accent-1':
        case 'accent-2':
            return { bg: 'var(--aqx-scheme-accent)', fg: 'var(--aqx-theme-accent-contrast, #fff)' };
        case 'background-2':
            return { bg: 'var(--aqx-scheme-surface)', fg: 'var(--aqx-scheme-text)' };
        case 'background-1':
        default:
            return { bg: 'var(--aqx-scheme-bg)', fg: 'var(--aqx-scheme-text)' };
    }
}
/**
 * Client reveal hook. SSR returns false (bar hidden). On mount, depending on `displayWhen`:
 *  - always       → reveal immediately.
 *  - after_scroll → IntersectionObserver on the in-page main buy button (#aqx-main-atc / [data-aqx-main-atc]
 *                   / the buy-buttons block) — reveal once it scrolls out of view above the viewport.
 *  - after_pixels → reveal once window scrollY ≥ N.
 *  - after_percent→ reveal once scrolled ≥ P% of the scrollable height.
 * Degrades to never-shown without JS (correct — the in-page button is the canonical CTA).
 */
function useStickyReveal(displayWhen, pixels, percent) {
    const [shown, setShown] = useState(false);
    useEffect(() => {
        if (displayWhen === 'always') {
            setShown(true);
            return;
        }
        if (displayWhen === 'after_scroll') {
            const target = document.querySelector('[data-aqx-main-atc]') ??
                document.querySelector('#aqx-main-atc') ??
                document.querySelector('[data-block-type="buy-buttons"]');
            if (target && typeof IntersectionObserver !== 'undefined') {
                const io = new IntersectionObserver((entries) => {
                    const e = entries[0];
                    if (!e)
                        return;
                    // Reveal once the button has scrolled ABOVE the viewport (passed), hide if back in view.
                    const passed = !e.isIntersecting && e.boundingClientRect.top < 0;
                    setShown(passed);
                }, { threshold: 0 });
                io.observe(target);
                return () => io.disconnect();
            }
            // No in-page button found → fall back to a fixed 500px scroll threshold.
            const onScroll = () => setShown(window.scrollY >= 500);
            onScroll();
            window.addEventListener('scroll', onScroll, { passive: true });
            return () => window.removeEventListener('scroll', onScroll);
        }
        // Pixel / percent thresholds.
        const onScroll = () => {
            if (displayWhen === 'after_pixels') {
                setShown(window.scrollY >= pixels);
            }
            else if (displayWhen === 'after_percent') {
                const doc = document.documentElement;
                const scrollable = doc.scrollHeight - doc.clientHeight;
                const pct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
                setShown(pct >= percent);
            }
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [displayWhen, pixels, percent]);
    return shown;
}
const StickyAtcBlock = ({ block, context }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    return (_jsx(StickyAtc, { bs: block.settings, ps: ps, blockId: block.id, context: context }));
};
function StickyAtc({ bs, ps, blockId, context, }) {
    const { product, variants, variantId, setVariantId, message } = ps;
    const cls = `aqx-sat-${blockId}`;
    // ── Reveal logic (General: display_when / display_pixels / display_percent). ──
    const displayWhen = str(bs.displayWhen, 'after_scroll');
    const displayPixels = num(bs.displayPixels, 500);
    const displayPercent = num(bs.displayPercent, 50);
    const shown = useStickyReveal(displayWhen, displayPixels, displayPercent);
    // ── Surface colours (color_scheme + per-device transparent bg). ──
    const { bg: schemeBg, fg: schemeFg } = schemeSurface(str(bs.colorScheme, 'background-1'));
    // ── Product mirror data. ──
    const price = product.price;
    const compareAt = product.compareAtPrice ?? null;
    const onSale = !!compareAt && compareAt.amount > price.amount;
    const cover = product.images?.[0] ?? null;
    const titleOverride = str(bs.title);
    const titleText = titleOverride || product.name;
    const subtitleText = str(bs.subtitle);
    const rating = typeof product.rating?.value === 'number'
        ? product.rating.value
        : 4.8;
    const starColor = resolveHex(bs.starColor, '#ffcc00');
    const starsLabel = str(bs.starsLabel, '(xxxx avis)');
    // ── Button (function + label + custom color + icons + scale + price-in-button). ──
    const fn = str(bs.function, 'add_to_cart');
    const addToCart = t(context, 'product.add_to_cart');
    const buttonLabel = str(bs.buttonLabel, addToCart) || addToCart;
    const customOn = bs.enableCustomBtnColor === true;
    const btnBg = customOn
        ? (resolveColor(bs.customBtnColor) ?? 'var(--aqx-scheme-accent)')
        : 'var(--aqx-scheme-accent)';
    const btnFg = 'var(--aqx-theme-accent-contrast, #fff)';
    const prefixIcon = imageUrl(bs.prefixIcon);
    const suffixIcon = imageUrl(bs.suffixIcon);
    const iconScale = num(bs.iconScale, 120) / 100;
    // ── Variants (per-device variant_picker toggle; single combined dropdown). ──
    const current = variants.find((v) => v.id === variantId) ?? variants[0];
    // ── Per-device toggle matrix. ──
    const d = {
        layout: str(bs.desktopLayout, 'full'),
        maxWidth: num(bs.desktopMaxWidth, 850),
        image: bs.desktopShowImage === true,
        title: bs.desktopShowTitle !== false,
        subtitle: bs.desktopShowSubtitle !== false,
        rating: bs.desktopRatingStars === true,
        price: bs.desktopShowPrice !== false,
        saleBadge: bs.desktopShowSaleBadge !== false,
        picker: bs.desktopVariantPicker === true,
        fullBtn: bs.desktopFullButtonWidth === true,
        priceInBtn: bs.desktopShowPriceInButton === true,
        transparent: bs.desktopTransparentBg === true,
    };
    const m = {
        layout: str(bs.mobileLayout, 'full'),
        image: bs.mobileShowImage === true,
        title: bs.mobileShowTitle !== false,
        subtitle: bs.mobileShowSubtitle !== false,
        rating: bs.mobileRatingStars === true,
        price: bs.mobileShowPrice !== false,
        saleBadge: bs.mobileShowSaleBadge !== false,
        picker: bs.mobileVariantPicker === true,
        fullBtn: bs.mobileFullButtonWidth === true,
        priceInBtn: bs.mobileShowPriceInButton === true,
        transparent: bs.mobileTransparentBg === true,
    };
    // Button action: scroll_* smooth-scrolls to the target selector. add_to_cart does nothing here — it used
    // to flash "Ajouté au panier (démo)." in the bar, a placeholder that reached real storefronts.
    const onAction = () => {
        if (fn.startsWith('scroll_')) {
            const selector = fn.slice('scroll_'.length);
            const el = typeof document !== 'undefined' ? document.querySelector(selector) : null;
            if (el)
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };
    // Variant picker control (a single combined dropdown of the product's variants).
    const variantPicker = variants.length > 1 ? (_jsx("select", { "aria-label": t(context, 'a11y.variant'), value: current?.id ?? '', onChange: (e) => setVariantId(e.target.value), className: `${cls}__variant`, style: {
            fontFamily: 'inherit',
            fontSize: '13px',
            padding: '6px 8px',
            borderRadius: 'var(--aqx-theme-radius-input, 6px)',
            border: '1px solid var(--aqx-scheme-border)',
            background: 'var(--aqx-scheme-bg)',
            color: 'var(--aqx-scheme-text)',
            maxInlineSize: '11rem',
        }, children: variants.map((v) => (_jsx("option", { value: v.id, disabled: !v.available, children: v.label }, v.id))) })) : null;
    // ── Scoped CSS: fixed bottom bar, hidden until `shown`; per-device class flags drive what shows. ──
    // LAYER 50 — the documented layer for the sticky buy bar (see sections/_shared/CountdownBar: the urgency
    // strip sits at 45, "deliberately BELOW the sticky buy bar (50)"). It was 40, i.e. UNDER that strip; the
    // sticky-cta section shared the same defect at 2 and got covered by page content. Both now sit at 50,
    // above every content layer and below the header drawer (55-75) and the modals (100+).
    // The desktop/mobile toggles are baked into element classes (`__d-only` / `__m-only` and per-feature
    // `is-d-*` / `is-m-*`) and switched at the 750px breakpoint, so each device reads its own matrix.
    const css = `
.${cls}{position:fixed;inset-inline:0;bottom:0;z-index:50;
  transform:translateY(110%);transition:transform .25s ease;
  padding:10px 16px;box-sizing:border-box;pointer-events:none;}
.${cls}.is-shown{transform:translateY(0);pointer-events:auto;}
.${cls}__inner{display:flex;align-items:center;gap:14px;margin-inline:auto;width:100%;
  background:${schemeBg};color:${schemeFg};
  border:1px solid var(--aqx-scheme-border);border-radius:var(--aqx-theme-radius-md,10px);
  box-shadow:0 -2px 18px rgba(0,0,0,.12);padding:10px 14px;box-sizing:border-box;}
.${cls}__info{display:flex;align-items:center;gap:12px;min-width:0;flex:1;}
.${cls}__text{display:flex;flex-direction:column;gap:2px;min-width:0;}
.${cls}__title{font-family:var(--aqx-theme-heading-font, var(--aqx-theme-font));font-weight:700;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.${cls}__sub{font-size:13px;opacity:.75;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.${cls}__thumb{width:46px;height:46px;border-radius:var(--aqx-theme-radius-media,0px);object-fit:cover;flex-shrink:0;
  border:1px solid var(--aqx-scheme-border);}
.${cls}__price{display:flex;align-items:baseline;gap:8px;font-weight:700;white-space:nowrap;}
.${cls}__compare{text-decoration:line-through;opacity:.55;font-weight:400;font-size:.85em;}
.${cls}__badge{font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;
  background:var(--aqx-scheme-accent);color:var(--aqx-theme-accent-contrast,#fff);
  padding:2px 7px;border-radius:var(--aqx-theme-radius-button,6px);white-space:nowrap;}
.${cls}__cta{display:inline-flex;align-items:center;justify-content:center;gap:.5em;cursor:pointer;
  border:1px solid transparent;min-block-size:44px;padding:0 22px;font-family:inherit;font-weight:700;
  line-height:1.2;border-radius:var(--aqx-theme-radius-button,6px);background:${btnBg};color:${btnFg};
  white-space:nowrap;}
.${cls}__cta img{height:${iconScale}em;width:auto;}
.${cls}__right{display:flex;align-items:center;gap:10px;flex-shrink:0;}
/* Desktop (default ≥750) reads the desktop matrix; mobile-only nodes hidden. */
.${cls} .is-m{display:none;}
.${cls}__inner{max-width:${d.layout === 'floating' ? `${d.maxWidth}px` : '100%'};}
${d.transparent ? `.${cls}__inner{background:transparent;border-color:transparent;box-shadow:none;}` : ''}
${d.fullBtn ? `.${cls}__cta{flex:1;}` : ''}
.${cls}__d-image{display:${d.image && cover ? 'block' : 'none'};}
.${cls}__d-title{display:${d.title ? 'flex' : 'none'};}
.${cls}__d-sub{display:${d.subtitle && subtitleText ? 'block' : 'none'};}
.${cls}__d-rating{display:${d.rating ? 'inline-flex' : 'none'};}
.${cls}__d-price{display:${d.price ? 'flex' : 'none'};}
.${cls}__d-badge{display:${d.saleBadge && onSale ? 'inline-block' : 'none'};}
.${cls}__d-picker{display:${d.picker && variantPicker ? 'inline-flex' : 'none'};}
.${cls}__d-pib{display:${d.priceInBtn ? 'inline' : 'none'};}
@media(max-width:749px){
  .${cls} .is-d{display:none!important;}
  .${cls} .is-m{display:revert;}
  .${cls}__inner{max-width:${m.layout === 'floating' ? '92%' : '100%'};
    ${m.transparent ? 'background:transparent;border-color:transparent;box-shadow:none;' : ''}}
  ${m.fullBtn ? `.${cls}__cta{flex:1;}` : `.${cls}__cta{flex:0;}`}
  .${cls}__d-image{display:none!important;}
  .${cls}__m-image{display:${m.image && cover ? 'block' : 'none'};}
  .${cls}__d-title{display:none!important;} .${cls}__m-title{display:${m.title ? 'flex' : 'none'};}
  .${cls}__d-sub{display:none!important;}   .${cls}__m-sub{display:${m.subtitle && subtitleText ? 'block' : 'none'};}
  .${cls}__d-rating{display:none!important;}.${cls}__m-rating{display:${m.rating ? 'inline-flex' : 'none'};}
  .${cls}__d-price{display:none!important;} .${cls}__m-price{display:${m.price ? 'flex' : 'none'};}
  .${cls}__d-badge{display:none!important;} .${cls}__m-badge{display:${m.saleBadge && onSale ? 'inline-block' : 'none'};}
  .${cls}__d-picker{display:none!important;}.${cls}__m-picker{display:${m.picker && variantPicker ? 'inline-flex' : 'none'};}
  .${cls}__m-pib{display:${m.priceInBtn ? 'inline' : 'none'};}
}
/* Builder device-preview mirror (container frames width without a viewport change). */
@container aqx-page (max-width:749px){
  .${cls} .is-d{display:none!important;} .${cls} .is-m{display:revert;}
  .${cls}__inner{max-width:${m.layout === 'floating' ? '92%' : '100%'};
    ${m.transparent ? 'background:transparent;border-color:transparent;box-shadow:none;' : ''}}
  .${cls}__d-image{display:none!important;}  .${cls}__m-image{display:${m.image && cover ? 'block' : 'none'};}
  .${cls}__d-title{display:none!important;}  .${cls}__m-title{display:${m.title ? 'flex' : 'none'};}
  .${cls}__d-sub{display:none!important;}    .${cls}__m-sub{display:${m.subtitle && subtitleText ? 'block' : 'none'};}
  .${cls}__d-rating{display:none!important;} .${cls}__m-rating{display:${m.rating ? 'inline-flex' : 'none'};}
  .${cls}__d-price{display:none!important;}  .${cls}__m-price{display:${m.price ? 'flex' : 'none'};}
  .${cls}__d-badge{display:none!important;}  .${cls}__m-badge{display:${m.saleBadge && onSale ? 'inline-block' : 'none'};}
  .${cls}__d-picker{display:none!important;} .${cls}__m-picker{display:${m.picker && variantPicker ? 'inline-flex' : 'none'};}
  .${cls}__m-pib{display:${m.priceInBtn ? 'inline' : 'none'};}
}
`.trim();
    const priceEl = (showCls) => (_jsxs("span", { className: `${cls}__price ${showCls}`, children: [_jsx("span", { children: context.formatMoney(price) }), onSale ? _jsx("span", { className: `${cls}__compare`, children: context.formatMoney(compareAt) }) : null] }));
    const titleBlock = (_jsxs("div", { className: `${cls}__text`, children: [_jsx("span", { className: `${cls}__title is-d ${cls}__d-title`, children: titleText }), _jsx("span", { className: `${cls}__title is-m ${cls}__m-title`, children: titleText }), subtitleText ? (_jsxs(_Fragment, { children: [_jsx("span", { className: `${cls}__sub is-d ${cls}__d-sub`, dangerouslySetInnerHTML: { __html: sanitizeRichtextHtml(subtitleText) } }), _jsx("span", { className: `${cls}__sub is-m ${cls}__m-sub`, dangerouslySetInnerHTML: { __html: sanitizeRichtextHtml(subtitleText) } })] })) : null, _jsxs("span", { className: `${cls}__rating is-d ${cls}__d-rating`, style: { alignItems: 'center', gap: 6 }, children: [_jsx(Stars, { color: starColor, rating: rating }), starsLabel ? _jsx("span", { style: { fontSize: 12, opacity: 0.75 }, children: starsLabel }) : null] }), _jsxs("span", { className: `${cls}__rating is-m ${cls}__m-rating`, style: { alignItems: 'center', gap: 6 }, children: [_jsx(Stars, { color: starColor, rating: rating }), starsLabel ? _jsx("span", { style: { fontSize: 12, opacity: 0.75 }, children: starsLabel }) : null] })] }));
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsxs("div", { className: `${cls}${shown ? ' is-shown' : ''}`, role: "region", "aria-label": t(context, 'a11y.sticky_add_to_cart'), "aria-hidden": !shown, children: [_jsxs("div", { className: `${cls}__inner`, 
                        // marginStyle emits desktop margins inline PLUS the --aqx-mmt/--aqx-mmb mobile vars; the global
                        // theme.css rule ([style*='--aqx-mmt']) swaps to them under ≤749px (both @media + @container).
                        style: marginStyle(bs, 0, 0), children: [_jsxs("div", { className: `${cls}__info`, children: [cover ? (_jsxs(_Fragment, { children: [_jsx("img", { className: `${cls}__thumb is-d ${cls}__d-image`, ...fixedWidthImgProps(cover.url, STICKY_THUMB_PAINTED_PX), alt: productImageAlt(cover.alt, titleText), loading: "lazy", decoding: "async" }), _jsx("img", { className: `${cls}__thumb is-m ${cls}__m-image`, ...fixedWidthImgProps(cover.url, STICKY_THUMB_PAINTED_PX), alt: productImageAlt(cover.alt, titleText), loading: "lazy", decoding: "async" })] })) : null, titleBlock] }), _jsxs("div", { className: `${cls}__right`, children: [onSale ? (_jsxs(_Fragment, { children: [_jsx("span", { className: `${cls}__badge is-d ${cls}__d-badge`, children: t(context, 'product.on_sale') }), _jsx("span", { className: `${cls}__badge is-m ${cls}__m-badge`, children: t(context, 'product.on_sale') })] })) : null, priceEl(`is-d ${cls}__d-price`), priceEl(`is-m ${cls}__m-price`), variantPicker ? (_jsxs(_Fragment, { children: [_jsx("span", { className: `${cls}__picker is-d ${cls}__d-picker`, children: variantPicker }), _jsx("span", { className: `${cls}__picker is-m ${cls}__m-picker`, children: variantPicker })] })) : null, _jsxs("button", { type: "button", className: `${cls}__cta`, onClick: onAction, children: [prefixIcon ? _jsx("img", { src: prefixIcon, alt: "", "aria-hidden": "true" }) : null, _jsx("span", { children: buttonLabel }), _jsxs("span", { className: `is-d ${cls}__d-pib`, children: [" \u00B7 ", context.formatMoney(price)] }), _jsxs("span", { className: `is-m ${cls}__m-pib`, children: [" \u00B7 ", context.formatMoney(price)] }), suffixIcon ? _jsx("img", { src: suffixIcon, alt: "", "aria-hidden": "true" }) : null] })] })] }), message ? (_jsx("p", { role: "status", className: `${cls}__msg`, style: {
                            position: 'absolute',
                            width: 1,
                            height: 1,
                            overflow: 'hidden',
                            clip: 'rect(0 0 0 0)',
                        }, children: message })) : null] })] }));
}
export const stickyAtcBlock = {
    type: 'sticky-atc',
    schema: stickyAtcBlockSchema,
    Component: StickyAtcBlock,
    meta: { label: 'Sticky Add To Cart' },
};
//# sourceMappingURL=index.js.map